import React, { useEffect, useMemo, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser, logout } from "../loginSlice";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import { fetchThemeData } from "../../admin/themeSlice";
import { apiUrl } from "../../utils/apiUrl";
import { themeButtonBackground, themeButtonTextColor } from "../../utils/colorUtils";
import Swal from "sweetalert2";

const LANDING_LOGO = "/img/logo.png";

function safeRedirectUrl(url) {
  const fallback =
    process.env.REACT_APP_BASE_URL ||
    (typeof window !== "undefined" ? window.location.origin : "/") ||
    "/";
  if (!url || typeof url !== "string") return fallback;
  try {
    return new URL(url, window.location.origin).href;
  } catch {
    return fallback;
  }
}

function isScoreRule(rule) {
  const s = String(rule || "").trim();
  return /^[+\-−–]/.test(s);
}

function normalizeRules(raw) {
  if (Array.isArray(raw)) {
    return raw.map((r) => String(r ?? "").trim()).filter(Boolean);
  }
  if (raw && typeof raw === "object") {
    return Object.keys(raw)
      .sort((a, b) => Number(a) - Number(b))
      .map((k) => String(raw[k] ?? "").trim())
      .filter(Boolean);
  }
  if (typeof raw === "string" && raw.trim()) {
    try {
      return normalizeRules(JSON.parse(raw));
    } catch {
      return [raw.trim()];
    }
  }
  return [];
}

const DEFAULT_INSTRUCTION_RULES = [
  "Read a situation. Pick exactly 2 or 3 values.",
  "Discover why they fit, then move to the next moment.",
];

function Login() {
  const dispatch = useDispatch();
  const { status, user, error } = useSelector((state) => state.auth);
  const { status: themeStatus, data: themeData, error: themeError } = useSelector(
    (state) => state.theme
  );

  const adminToken = useSelector(selectAdminToken);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAdmin = searchParams.get("admin") === "true";
  const isFromMobileApp = searchParams.get("fromMobileApp") === "true";
  const mobileAppQuery = isFromMobileApp ? "?fromMobileApp=true" : "";
  const alreadyPlayedHandledRef = useRef(false);

  useEffect(() => {
    const otp = searchParams.get("otp");
    const demo = searchParams.get("demo");
    if (otp || demo) {
      dispatch(logout());
    }
    dispatch(loginUser());
  }, [dispatch, searchParams]);

  useEffect(() => {
    if (status === "succeeded" && user && isAdmin) {
      navigate("/admin");
    }
  }, [status, user, isAdmin, navigate]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    if (status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect = safeRedirectUrl(user?.backButtonRedirect);
    const title =
      msg.includes("OTP is required") || msg.includes("No saved user")
        ? "Login required"
        : "Session Expired!";
    Swal.fire(title, msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  useEffect(() => {
    if (isAdmin) return;
    if (status !== "succeeded" || !user) return;
    if (alreadyPlayedHandledRef.current) return;
    if (!adminToken) return;

    const redirect = safeRedirectUrl(user.backButtonRedirect);

    const showOnceAndRedirect = async () => {
      if (alreadyPlayedHandledRef.current) return;
      alreadyPlayedHandledRef.current = true;
      await Swal.fire({
        title: "You have already played!",
        icon: "error",
        timer: 3000,
        timerProgressBar: true,
        allowOutsideClick: false,
        showConfirmButton: true,
        confirmButtonText: "OK",
      });
      if (isFromMobileApp) {
        window.GameStatus?.postMessage?.("navigate_back");
      } else {
        window.location.href = redirect;
      }
    };

    let cancelled = false;
    (async () => {
      try {
        const resp = await fetch(apiUrl("/spotGameStatus"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: user.userId,
            sessionId: user.sessionId,
            organizationId: user.organizationId,
          }),
        });
        const data = await resp.json();
        if (cancelled || alreadyPlayedHandledRef.current) return;
        if (data.success && data.completed) {
          await showOnceAndRedirect();
        }
      } catch (e) {
        console.error("already-played check on login:", e);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [status, user, adminToken, isAdmin, isFromMobileApp]);

  const retryThemeLoad = () => {
    dispatch(fetchThemeData({ themeId: null }));
  };

  const textColor = String(themeData?.text_color || "#ffffff").trim() || "#ffffff";
  const btnBg = themeButtonBackground(themeData) || "#3B82F6";
  const btnColor = themeButtonTextColor(themeData) || "#ffffff";
  const btnStyle = {
    backgroundColor: btnBg,
    color: btnColor,
    border: `2px solid ${btnBg}`,
  };

  const gameTitle =
    themeData?.game_page_title ??
    themeData?.game_title ??
    "Every situation needs a different kind of strength. Which colours would you choose?";

  const allRules = normalizeRules(themeData?.rules);
  const instructionFromTheme = allRules.filter((r) => !isScoreRule(r));
  const instructionRules =
    instructionFromTheme.length > 0 ? instructionFromTheme : DEFAULT_INSTRUCTION_RULES;
  const scoreRules = allRules.filter((r) => isScoreRule(r));

  const colourDots = useMemo(() => {
    const list = Array.isArray(themeData?.colors) ? themeData.colors : [];
    return list
      .slice(0, 9)
      .map((c) => String(c?.color_code || "").trim())
      .filter(Boolean);
  }, [themeData]);

  const colourCount = colourDots.length || 9;
  const availableSituations = Array.isArray(themeData?.questions)
    ? themeData.questions.length
    : Number(themeData?.total_questions) || 0;
  const requestedSituations = parseInt(String(themeData?.display_question_count ?? "10"), 10);
  const situationCount = availableSituations
    ? Math.min(
        availableSituations,
        Math.max(
          1,
          Number.isFinite(requestedSituations) && requestedSituations > 0
            ? requestedSituations
            : Math.min(10, availableSituations)
        )
      )
    : 10;

  const correctPts = Number(themeData?.correct_points) || 10;
  const wrongPts = Number(themeData?.wrong_points ?? themeData?.wromg_points) || 5;

  const scorePills =
    scoreRules.length > 0
      ? scoreRules
      : [`+${correctPts} per correct colour`, `−${wrongPts} per incorrect colour`];

  if (status === "loading" || themeStatus === "loading") {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  if (status === "succeeded" && user && themeStatus === "failed") {
    return (
      <div className="login-main-container">
        <p className="login-theme-error">
          Could not load game theme. Make sure the backend is running on port 5001.
          {themeError ? ` (${themeError})` : ""}
        </p>
        <button type="button" className="begin-play-btn" onClick={retryThemeLoad}>
          Retry
        </button>
      </div>
    );
  }

  const ready = user && status === "succeeded" && themeStatus === "succeeded" && themeData;

  return (
    <div className="login-main-container">
      {ready ? (
        <div className="coc-landing-card" style={{ color: textColor }}>
          <p className="coc-landing-eyebrow">
            {colourCount} VALUES · {situationCount} MOMENTS OF CHOICE
          </p>

          {colourDots.length > 0 ? (
            <div className="coc-landing-dots" aria-hidden>
              {colourDots.map((code, i) => (
                <span
                  key={`${code}-${i}`}
                  className="coc-landing-dot"
                  style={{ background: code }}
                />
              ))}
            </div>
          ) : null}

          {/* 1. Logo */}
          <img
            src={LANDING_LOGO}
            className="login-logo coc-landing-logo"
            alt="Colours of Choice"
          />

          {/* 2. Game title */}
          <p className="coc-landing-title" style={{ color: textColor }}>
            {gameTitle}
          </p>

          {/* 3. Rules */}
          {instructionRules.length > 0 ? (
            <div className="coc-landing-rules" style={{ color: textColor }}>
              {instructionRules.map((rule, i) => (
                <p key={i}>{rule}</p>
              ))}
            </div>
          ) : null}

          <div className="coc-landing-pills">
            {scorePills.map((pill, i) => (
              <span key={i} className="coc-landing-pill" style={{ color: textColor, borderColor: textColor }}>
                {pill}
              </span>
            ))}
          </div>

          {/* 4. Let's Play */}
          <Link to={`/game${mobileAppQuery}`} className="begin-play-btn coc-landing-cta" style={btnStyle}>
            LET&apos;S PLAY
          </Link>
        </div>
      ) : (
        <img src={LANDING_LOGO} className="login-logo" alt="Colours of Choice" />
      )}
    </div>
  );
}

export default Login;
